// @givmo/app-admin — Settings → "Public giving card" tab (CW-WEB4, the admin publish half).
//
// Where a company Owner decides whether — and what — of the company's giving may be shown on a page
// Givmo does not control, and collects the code to paste there.
//
// ══ WHY IT IS A SETTINGS TAB AND NOT A SIDEBAR DESTINATION ════════════════════════════════════
// It is the same question Privacy Settings asks, at a different scope: that tab governs what may be
// seen about this company INSIDE Givmo, this one governs what may be seen about it OUTSIDE. Both are
// Owner-gated company settings, both are one whole-state write against `/admin/company/*`, and this
// one has no list, no sub-route and no second screen. The sidebar's dark entries are AREAS
// (fundraisers, gift cards, competitions); a single consent screen registered through the settings
// tab registry (`agent_docs/admin-settings-tabs.md`) costs no nav surface and cannot disturb the
// pinned `NAV_ITEMS` contract that e2e selects on.
//
// ══ THE PREVIEW IS THE PUBLIC DOCUMENT, NOT A COPY OF IT ══════════════════════════════════════
// ⚠ The preview frames `apps/embed`'s own `/w/{wgt_id}` — the very document a visitor gets. This app
// renders NO company-card markup, holds no copy of the card's money law, and never decodes the
// public payload. That is the whole design, and it buys three things a re-implementation could not:
//
//   1. FIDELITY BY IDENTITY. "The preview matches the public render" is not an assertion anyone has
//      to keep true — it is the same bytes, from the same server, through the same k-anonymity floor.
//   2. POST-SUPPRESSION TRUTH FOR FREE. A block the floor suppressed is absent from the preview
//      because it is absent from the payload. An unpublished card previews as the unavailable
//      fallback, because that is what the public gets.
//   3. THE POPULATION ORACLE IS STRUCTURALLY UNREACHABLE. `modules` is the OWNER's selection; the
//      rendered block is what survived the floor. Gating a rendered block on `modules` would draw a
//      heading for a module the floor suppressed and leak the company's donor population — the
//      documented trap on this family. There is nothing here to gate: the checkboxes are the
//      selection, and the card is an iframe.
//
// So the module list below never claims a block WILL appear. It says a block MAY appear, and the
// preview says what actually does.
//
// ══ DARK ══════════════════════════════════════════════════════════════════════════════════════
// The tab is invisible unless `VITE_COMPANY_GIVING_CARD_ENABLED` is on AND the caller is an Owner
// (`canSeeGivingCardTab`). Its two routes 404 while the backend `CONNECT_WIDGETS_ENABLED` flag is
// dark, which this treats as "not available in this environment" rather than as a failure — the
// backend maps that flag's exception to the not-found handler precisely so a dark surface is
// indistinguishable from a route that does not exist.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError, supportReference } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Skeleton,
  Switch,
  testIdProp,
  toast,
} from '@givmo/design-system';
import {
  isCompanyGivingCardEnabled,
  widgetEmbedBaseUrl,
  widgetLoaderScriptUrl,
} from '../../lib/env';
import type {
  SettingsTabContext,
  SettingsTabDefinition,
  SettingsTabPermissionContext,
} from '../settings/settings-tab-registry';
import {
  DEFAULT_GIVING_MODULES,
  GIVING_MODULES,
  decodeGivingPublication,
  isSaveableSelection,
  toSaveModules,
  zeroGivingPublicationState,
  type GivingPublicationState,
} from './giving-card-decode';
import {
  IFRAME_FALLBACK_HEIGHT,
  buildEmbedDocumentUrl,
  buildIframeSnippet,
  buildScriptSnippet,
} from './giving-card-snippet';
import { fetchGivingPublication, saveGivingPublication } from './giving-card-transport';

const PREFIX = 'settings-giving-card';

/** The registered tab id — kebab-case, part of the `settings-tab-<id>` testid contract, never renamed. */
export const GIVING_CARD_TAB_ID = 'giving-card';

/** The publication query's key. One entry; the save writes it back rather than invalidating. */
export const GIVING_PUBLICATION_QUERY_KEY = ['admin-company-giving-publication'] as const;

const INTRO =
  'Publish a card showing your company’s giving, and embed it on your own site — a careers page, ' +
  'an about page, a recruiting microsite. The card shows figures only: no employee is named, no ' +
  'individual donation appears, and a block stays hidden until enough people have taken part for ' +
  'it to be reported without identifying anyone.';

const FRAME_TITLE_SUFFIX = 'giving on Givmo';

/**
 * Whether the Settings tab is visible to this caller.
 *
 * Both halves fail CLOSED, and on `role === null` too. The tab strip renders before
 * `GET /admin/company` and the admin-role read resolve, and on a failed read the role stays `null` —
 * so hiding until the value lands is what keeps a privileged tab from flashing at a Member. It
 * reappears when the read succeeds.
 *
 * `source` is injectable so the flag half is testable; the registered predicate below passes the real
 * `import.meta.env`.
 */
export function canSeeGivingCardTab(
  context: SettingsTabPermissionContext,
  source: ImportMetaEnv = import.meta.env,
): boolean {
  return isCompanyGivingCardEnabled(source) && context.role === 'Owner';
}

/** The registry entry. Order 50 — the core four hold 10/20/30/40, so this appends. */
export const GIVING_CARD_SETTINGS_TAB: SettingsTabDefinition = {
  id: GIVING_CARD_TAB_ID,
  label: 'Public giving card',
  order: 50,
  permission: (context) => canSeeGivingCardTab(context),
  component: SettingsGivingCardTab,
};

export interface SettingsGivingCardTabProps extends SettingsTabContext {
  /** Injectable env for tests. Production passes nothing and the readers take `import.meta.env`. */
  env?: ImportMetaEnv;
}

/**
 * Whether to retry a failed publication read.
 *
 * A 404 is the DARK-SURFACE answer, not a transport hiccup — CW-BE6 maps its disabled-flag exception
 * to the not-found handler so an off feature looks exactly like a route that does not exist — and no
 * number of retries will turn a flag on. Everything else takes ONE retry, matching the app-wide
 * default (`lib/query-client.ts`) rather than quietly giving this one feature a different policy.
 */
export function shouldRetryPublicationRead(failureCount: number, error: unknown): boolean {
  if (isGivmoError(error) && error.status === 404) return false;
  return failureCount < 1;
}

/**
 * The editable part of the state, as one comparable string.
 *
 * `widget_id` and `published_at` are deliberately OUT: neither is editable here, and including them
 * would make a save's own response read as an unsaved change (the first save mints the widget, so
 * the id arrives with the answer).
 */
function stateSignature(state: GivingPublicationState): string {
  return `${state.published ? '1' : '0'}:${[...state.modules].sort().join(',')}`;
}

export function SettingsGivingCardTab({ company, env }: SettingsGivingCardTabProps) {
  const queryClient = useQueryClient();
  const embedBase = widgetEmbedBaseUrl(env ?? import.meta.env);
  const loaderUrl = widgetLoaderScriptUrl(env ?? import.meta.env);

  const publicationQuery = useQuery({
    queryKey: GIVING_PUBLICATION_QUERY_KEY,
    retry: shouldRetryPublicationRead,
    queryFn: async (): Promise<GivingPublicationState> =>
      decodeGivingPublication(await fetchGivingPublication()),
  });

  // The DRAFT the Owner is editing, seeded from the server state.
  //
  // Re-seeded on the server state's SIGNATURE rather than its object identity: a refetch that returns
  // the same publication produces a new object, and re-seeding on that would silently discard edits
  // the Owner had made but not yet saved. Only a genuinely different server answer replaces them.
  const saved = publicationQuery.data;
  const savedSignature = saved === undefined ? null : stateSignature(saved);
  const [draft, setDraft] = useState<GivingPublicationState>(zeroGivingPublicationState);
  useEffect(() => {
    if (saved !== undefined) setDraft(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the signature ON PURPOSE (above).
  }, [savedSignature]);

  const saveMutation = useMutation({
    mutationFn: async (next: GivingPublicationState): Promise<GivingPublicationState> =>
      decodeGivingPublication(
        await saveGivingPublication({ published: next.published, modules: toSaveModules(next) }),
      ),
    // The PUT returns the saved state, so it is written straight into the cache: the snippet and the
    // preview read the freshly minted `widget_id` with no second round trip, and the `dirty` check
    // below compares against what the server actually stored rather than what was sent.
    onSuccess: (result) => {
      queryClient.setQueryData(GIVING_PUBLICATION_QUERY_KEY, result);
      setDraft(result);
      toast.success(result.published ? 'Your giving card is published' : 'Your giving card is off');
    },
    onError: (error) => {
      toast.error(
        isGivmoError(error) ? error.message : 'Could not save your giving card settings.',
      );
    },
  });

  const dirty = savedSignature !== null && savedSignature !== stateSignature(draft);
  const saveable = isSaveableSelection(draft.modules);

  // ⚠ SAVE IS NOT GATED ON `dirty` ALONE, and the two extra cases are the ones a "no unsaved
  // changes" rule would strand:
  //
  //   - The FIRST save on a company that accepts the defaults exactly. Nothing is dirty, but the
  //     save is what MINTS the embed row — so without this an Owner who wants a card with the
  //     default blocks could never create one.
  //   - The MINT RETRY. CW-BE6 mints best-effort: a failure leaves the publication saved and
  //     UNBOUND, and the next save retries. That next save is by definition not dirty, and the
  //     screen tells the Owner to "save again" — so the button has to actually be there.
  //
  // Both reduce to the same fact — no widget is bound yet — and a redundant save is a whole-state
  // restatement of what the server already holds, which is a no-op by construction.
  const canSave = saveable && (dirty || (saved !== undefined && saved.widgetId === null));

  const frameTitle = `${company.name ? `${company.name}’s ` : 'Our '}${FRAME_TITLE_SUFFIX}`;
  const publishedAtLabel = draft.publishedAt === null ? '' : publishedOnLabel(draft.publishedAt);
  const previewUrl = useMemo(
    () => buildEmbedDocumentUrl(embedBase, draft.widgetId),
    [embedBase, draft.widgetId],
  );
  const scriptSnippet = useMemo(
    () => buildScriptSnippet(loaderUrl, draft.widgetId),
    [loaderUrl, draft.widgetId],
  );
  const iframeSnippet = useMemo(
    () => buildIframeSnippet(embedBase, draft.widgetId, frameTitle),
    [embedBase, draft.widgetId, frameTitle],
  );

  if (publicationQuery.isPending) {
    return (
      <div className="flex flex-col gap-12 pt-16" {...testIdProp(`${PREFIX}-loading`)}>
        <Skeleton className="h-12 w-1/3" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-2/3" />
      </div>
    );
  }

  if (publicationQuery.isError) {
    const error = publicationQuery.error;
    // A 404 is the dark-surface answer, not a failure: CW-BE6 maps the disabled-flag exception to the
    // not-found handler so an off feature looks exactly like a route that does not exist. Offering a
    // "Try again" here would invite an Owner to retry something that cannot succeed today.
    if (isGivmoError(error) && error.status === 404) {
      return (
        <div className="flex flex-col gap-8 pt-16" {...testIdProp(`${PREFIX}-unavailable`)}>
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
            Public giving cards are not available on this environment yet.
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-start gap-4 pt-16" {...testIdProp(`${PREFIX}-error`)}>
        <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
          {isGivmoError(error) ? error.message : 'Failed to load your giving card settings.'}
        </p>
        {isGivmoError(error) && supportReference(error) !== null && (
          <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
            {supportReference(error)}
          </p>
        )}
        <button
          type="button"
          onClick={() => void publicationQuery.refetch()}
          className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
          {...testIdProp(`${PREFIX}-error-retry`)}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-16 pt-16" {...testIdProp(`${PREFIX}-tab`)}>
      <p className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
        {INTRO}
      </p>

      <Card>
        <CardHeader title="What you publish" />
        <CardBody>
          <div className="flex flex-col gap-16">
            <label className="flex items-start gap-12">
              <Switch
                appearance="saas"
                checked={draft.published}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({ ...current, published: checked }))
                }
                testId={`${PREFIX}-published`}
              />
              <span className="flex min-w-0 flex-col gap-2">
                <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                  Show our giving publicly
                </span>
                <span className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
                  Turning this off takes the card down everywhere it is embedded, within an hour.
                </span>
              </span>
            </label>

            <fieldset className="flex flex-col gap-12 border-0 p-0">
              <legend className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                Blocks to show
              </legend>
              <p className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
                Pick what the card may show. A block you pick still stays hidden until enough people
                have taken part for it to be reported without identifying anyone — the preview below
                is what visitors actually see.
              </p>

              {GIVING_MODULES.map((module) => {
                const checked = draft.modules.includes(module.id);
                return (
                  <label key={module.id} className="flex items-start gap-12">
                    <Checkbox
                      appearance="saas"
                      checked={checked}
                      onCheckedChange={(next) =>
                        setDraft((current) => ({
                          ...current,
                          modules: next
                            ? [...current.modules, module.id]
                            : current.modules.filter((id) => id !== module.id),
                        }))
                      }
                      testId={module.testId}
                    />
                    <span className="flex min-w-0 flex-col gap-2">
                      <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                        {module.label}
                      </span>
                      <span className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
                        {module.description}
                      </span>
                    </span>
                  </label>
                );
              })}

              {/* A whole-state save that dropped a block this build cannot draw would revoke it
                  without the Owner asking. They are carried through untouched — said out loud, so
                  "unchanged" is a statement rather than a silence. */}
              {draft.unrecognizedModules.length > 0 && (
                <p
                  className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-tertiary"
                  {...testIdProp(`${PREFIX}-unrecognized`)}
                >
                  {draft.unrecognizedModules.length === 1
                    ? 'One published block is newer than this screen and is left unchanged when you save.'
                    : `${draft.unrecognizedModules.length} published blocks are newer than this screen and are left unchanged when you save.`}
                </p>
              )}

              {!saveable && (
                <p
                  className="font-sans text-s13 font-w500 tracking-normal text-danger-700"
                  {...testIdProp(`${PREFIX}-empty-selection`)}
                >
                  Pick at least one block. To show nothing at all, turn “Show our giving publicly”
                  off instead.
                </p>
              )}
            </fieldset>

            <div className="flex items-center gap-12">
              <Button
                appearance="saas"
                tone="primary"
                disabled={!canSave}
                loading={saveMutation.isPending}
                onClick={() => saveMutation.mutate(draft)}
                testId={`${PREFIX}-save`}
              >
                Save
              </Button>
              {/* `publishedOnLabel` returns '' for an unparseable instant, and an empty testid'd span
                  is worse than no span: a spec asserting on the testid would pass against a blank. */}
              {draft.published && publishedAtLabel !== '' && (
                <span
                  className="font-sans text-s13 font-w400 tracking-normal text-text-tertiary"
                  {...testIdProp(`${PREFIX}-published-at`)}
                >
                  {publishedAtLabel}
                </span>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      <PreviewSection previewUrl={previewUrl} frameTitle={frameTitle} widgetId={draft.widgetId} />

      <EmbedCodeSection
        widgetId={draft.widgetId}
        scriptSnippet={scriptSnippet}
        iframeSnippet={iframeSnippet}
      />
    </div>
  );
}

/**
 * "Published on 2 August 2026", or nothing at all for an unreadable instant.
 *
 * The date is informational, so an unparseable value renders no line rather than the raw string or
 * an "Invalid Date" — a settings screen that displays its own confusion teaches an Owner to distrust
 * the rest of it.
 */
function publishedOnLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return `Published on ${at.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })}`;
}

/**
 * The live preview.
 *
 * The frame is the public embed document itself (see the file header), so there is no fidelity claim
 * to maintain here and no state to keep in step: it re-fetches whenever its `src` changes, and its
 * `src` changes when the widget does. What it shows for an UNPUBLISHED card is the same graceful
 * fallback a visitor gets, which is the honest preview of "off".
 *
 * `sandbox` matches the loader's, for the reasons documented there — dropping `allow-same-origin`
 * would give the frame an opaque origin, which the embed's own `script-src 'self'` cannot match, and
 * the card would render but never hydrate.
 */
function PreviewSection({
  previewUrl,
  frameTitle,
  widgetId,
}: {
  previewUrl: string | null;
  frameTitle: string;
  widgetId: string | null;
}) {
  return (
    <Card>
      <CardHeader
        title="Preview"
        subtitle="Exactly what a visitor sees — the same page your embed serves."
      />
      <CardBody>
        {previewUrl !== null ? (
          <iframe
            src={previewUrl}
            title={frameTitle}
            width="100%"
            height={IFRAME_FALLBACK_HEIGHT}
            style={{ border: 0, display: 'block' }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="strict-origin-when-cross-origin"
            {...testIdProp('giving-card-preview-frame')}
          />
        ) : (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
            {...testIdProp('giving-card-preview-unavailable')}
          >
            {widgetId === null
              ? 'Save once to create your card, and the preview appears here.'
              : 'The preview is not available on this environment yet.'}
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/** The two embed forms, each independently available: they depend on different CW-GO1 hosts. */
function EmbedCodeSection({
  widgetId,
  scriptSnippet,
  iframeSnippet,
}: {
  widgetId: string | null;
  scriptSnippet: string | null;
  iframeSnippet: string | null;
}) {
  return (
    <Card>
      <CardHeader
        title="Embed code"
        subtitle="Paste this into your own site. The card keeps itself up to date."
      />
      <CardBody>
        {widgetId === null ? (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
            {...testIdProp('giving-card-embed-pending')}
          >
            Save once to create your card and its embed code. If you have already saved, save again
            — the card is created on save.
          </p>
        ) : scriptSnippet === null && iframeSnippet === null ? (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
            {...testIdProp('giving-card-embed-unavailable')}
          >
            Embed code is not available on this environment yet.
          </p>
        ) : (
          <div className="flex flex-col gap-20">
            {scriptSnippet !== null && (
              <SnippetBlock
                heading="Recommended"
                note="One script tag per page, however many cards follow. The card resizes itself as your giving grows."
                snippet={scriptSnippet}
                copyLabel="Copy the script embed code"
                testId="giving-card-snippet-script"
              />
            )}
            {iframeSnippet !== null && (
              <SnippetBlock
                heading="If your site cannot run third-party JavaScript"
                // Said plainly because nothing on the page will correct it: with no loader listening
                // for the frame's height announcement, the height is whatever the host set.
                note="The same card, framed directly. This one does not resize itself — the frame is sized for the tallest card your blocks can produce, so trim “height” if it leaves a gap."
                snippet={iframeSnippet}
                copyLabel="Copy the plain iframe embed code"
                testId="giving-card-snippet-iframe"
              />
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * One snippet, on screen and selectable, with a copy convenience beside it.
 *
 * ⚠ `copyLabel` is REQUIRED, not decorative. The two buttons are visually identical "Copy" chips
 * distinguished only by which block they sit in — which a sighted reader gets from proximity and a
 * screen-reader user, scanning by control, does not. Two controls announcing "Copy" on one screen is
 * a coin flip between two genuinely different snippets, so each states which one it copies.
 */
function SnippetBlock({
  heading,
  note,
  snippet,
  copyLabel,
  testId,
}: {
  heading: string;
  note: string;
  snippet: string;
  copyLabel: string;
  testId: string;
}) {
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(snippet);
      toast.success('Embed code copied');
    } catch {
      // The code is on screen and selectable, so a refused clipboard is a missing convenience rather
      // than a dead end — say which it is instead of a bare failure.
      toast.error('Could not copy. Select the code and copy it manually.');
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-start justify-between gap-12">
        <div className="flex min-w-0 flex-col gap-2">
          <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">{heading}</p>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
            {note}
          </p>
        </div>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          aria-label={copyLabel}
          onClick={() => void copy()}
          testId={`${testId}-copy`}
        >
          Copy
        </Button>
      </div>
      <pre
        className="overflow-x-auto rounded-md bg-surface-sunken p-12 font-mono text-s12 leading-h15 tracking-normal text-text"
        {...testIdProp(testId)}
      >
        <code>{snippet}</code>
      </pre>
    </div>
  );
}

/** Re-exported so a caller can seed a test/fixture without reaching into the decode module. */
export { DEFAULT_GIVING_MODULES };
